import { reactive } from 'vue';
import { plural } from '../../helpers/plural';

/**
 * The policy types a segment can be built on, named by the hub's policy lines.
 *
 * A rule stores the SSO policy *type* — `auto`, `home` — and always has: that
 * key is what the gateway filters on, and rewriting it on a rename would
 * quietly stop every saved segment from matching. What XN-4535 changed is where
 * the *label* comes from. It used to be this list, hard-coded, with the comment
 * "no backend endpoint exists for this at launch"; a hub that called the line
 * "Car insurance" still saw "Auto" here, and the two wordings drifted apart.
 *
 * A module-level registry rather than a lookup threaded through nine exported
 * functions: every caller of `policyTypeLabel` is a pure formatter, and the
 * alternative was changing all of their signatures to carry a list that only
 * one of them reads. `usePolicyLinesStore` fills it on load.
 */
export const POLICY_TYPES = reactive([]);

/**
 * Replace the registry in place — the array identity is what every importer
 * holds, so it is mutated rather than reassigned.
 */
/**
 * Only lines carrying an SSO policy type appear. A line the owner invented has
 * no key, and the resolver behind this builder filters policies by that key —
 * so offering it would build a segment that matches nobody.
 */
export const setPolicyTypes = (lines) => {
  POLICY_TYPES.splice(
    0,
    POLICY_TYPES.length,
    ...(lines ?? [])
      .filter((line) => line.gateway_key && line.name && !line.archived)
      .map((line) => ({ value: line.gateway_key, label: line.name })),
  );
};

// Empty until the lines load, so a caller that needs a value before then gets
// '' rather than a type this hub may not sell.
const DEFAULT_POLICY_TYPE = '';

export const CONDITION_FIELDS = [
  { value: 'state', label: 'State' },
  { value: 'policy_type', label: 'Policy type' },
  { value: 'renewal_date', label: 'Renewal date' },
  { value: 'last_active', label: 'Last active' },
];

const IS = { value: 'is', label: 'is' };
const IS_NOT = { value: 'is_not', label: 'is not' };
const BEFORE = { value: 'before', label: 'before' };

// field -> the ops the pinned rule schema allows for it, in the prototype's
// order. "within" is one of several on State and Policy type, a number of days
// on the dates; nothing comes "before" a state or a policy type.
export const OPS_BY_FIELD = {
  state: [IS, IS_NOT, { value: 'within', label: 'within' }],
  policy_type: [IS, IS_NOT, { value: 'within', label: 'within' }],
  renewal_date: [IS, IS_NOT, { value: 'within_next_days', label: 'within' }, BEFORE, { value: 'after', label: 'after' }],
  last_active: [IS, IS_NOT, { value: 'within_last_days', label: 'within' }, BEFORE],
};

const DEFAULT_OP = { renewal_date: 'within_next_days', last_active: 'within_last_days' };

export const takesDays = (op) => op === 'within_next_days' || op === 'within_last_days';

const policyTypeLabel = (value) => POLICY_TYPES.find((p) => p.value === value)?.label || value;
/**
 * The types to offer for a rule: the hub's current lines, plus any type the
 * rule already carries that is no longer offered — a saved segment must keep
 * showing what it was built on. Deduplicated, so a repeated entry in a stored
 * rule cannot render the same option twice and confuse the multi-select.
 */
export const policyTypeOptions = (value) => {
  const offered = new Set(POLICY_TYPES.map((policyType) => policyType.value));
  const legacy = [...new Set(policyTypeList(value))].filter((entry) => !offered.has(entry));

  return [
    ...legacy.map((entry) => ({ value: entry, label: policyTypeLabel(entry) })),
    ...POLICY_TYPES,
  ];
};
const fieldLabel = (value) => CONDITION_FIELDS.find((f) => f.value === value)?.label || value;
const opLabel = (field, op) => (OPS_BY_FIELD[field] || []).find((o) => o.value === op)?.label || op;

export const defaultOpForField = (field) => DEFAULT_OP[field] ?? 'is';

// within_next_days defaults to 90: a fixed calendar cutoff would make the
// segment empty out the day it passes, so the default condition is one that
// recalculates relative to "now" on every evaluation instead of going stale.
const DEFAULT_DAYS = { within_next_days: 90, within_last_days: 30 };

export const defaultValueForField = (field, op) => {
  if (field !== 'policy_type') return DEFAULT_DAYS[op] ?? '';
  const first = POLICY_TYPES[0]?.value ?? DEFAULT_POLICY_TYPE;
  return op === 'within' ? [first].filter(Boolean) : first;
};

/** A State "within" value is typed as one comma-separated line. */
const listItems = (value) => String(value ?? '').split(',').map((item) => item.trim()).filter(Boolean);

export const createCondition = (field = 'policy_type') => {
  const op = defaultOpForField(field);
  return { field, op, value: defaultValueForField(field, op) };
};

const isPositiveInteger = (value) => Number.isInteger(value) && value > 0;
const isDateString = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const DATE_FIELD_VALUE = {
  within_next_days: isPositiveInteger,
  within_last_days: isPositiveInteger,
  before: isDateString,
  after: isDateString,
  is: isDateString,
  is_not: isDateString,
};

const isPolicyTypeValue = (op, value) => {
  const offered = (entry) => policyTypeOptions(entry).some((p) => p.value === entry);
  if (op !== 'within') return offered(value);
  return Array.isArray(value) && value.length > 0 && value.every(offered);
};

export const isConditionComplete = (condition) => {
  if (!condition) return false;
  const { field, op, value } = condition;
  if (!(OPS_BY_FIELD[field] || []).some((o) => o.value === op)) return false;
  if (field === 'policy_type') return isPolicyTypeValue(op, value);
  if (field === 'state') return typeof value === 'string' && listItems(value).length > 0;
  return DATE_FIELD_VALUE[op](value);
};

/** One type or several — segments saved before multi-select hold a bare string. */
export const policyTypeList = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean);

const policyTypeLabels = (value) => policyTypeList(value).map(policyTypeLabel);

const isRolling = (c) => takesDays(c.op);

const orList = (items) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} or ${items.at(-1)}` : items[0] ?? '');

const conditionPhrase = (c) => {
  if (c.op === 'within_next_days') return `${fieldLabel(c.field)} within the next ${plural(c.value, 'day')}`;
  if (c.op === 'within_last_days') return `${fieldLabel(c.field)} within the last ${plural(c.value, 'day')}`;
  if (c.op === 'within') {
    const items = c.field === 'policy_type' ? policyTypeLabels(c.value) : listItems(c.value);
    return `${fieldLabel(c.field)} is ${orList(items)}`;
  }
  const value = c.field === 'policy_type' ? policyTypeLabel(c.value) : c.value;
  return `${fieldLabel(c.field)} ${opLabel(c.field, c.op)} ${value}`;
};

export const summarizeRule = (rule) => {
  if (!rule) return '';
  if (rule.definition_type === 'policy_type') {
    const labels = policyTypeLabels(rule.policy_type).map((label) => label.toLowerCase());
    return labels.length ? `Policy type: ${labels.join(', ')}` : 'Policy type: none chosen';
  }
  if (rule.definition_type !== 'custom_rule') return '';

  const conditions = rule.conditions || [];
  const head = conditions.some(isRolling) ? 'Custom rule (rolling)' : 'Custom rule';
  const fixed = conditions.filter((c) => !isRolling(c)).map(conditionPhrase);

  return [head, ...fixed].join(' · ');
};

/**
 * The rule as a sentence, for the panel that says who a segment will include.
 * Deliberately not summarizeRule(): that is a dense label for a list row, and
 * this is the reassurance that the thing you just defined means what you meant.
 */
export const describeRule = (rule) => {
  if (!rule) return '';

  if (rule.definition_type === 'policy_type') {
    const labels = policyTypeLabels(rule.policy_type).map((label) => label.toLowerCase());
    if (!labels.length) return 'Pick at least one policy type.';

    const article = /^[aeiou]/.test(labels[0]) ? 'an' : 'a';

    return `Everyone holding ${article} ${labels.join(' or ')} policy.`;
  }

  if (rule.definition_type === 'custom_rule') {
    const conditions = rule.conditions || [];
    if (!conditions.length) return 'Add at least one condition.';

    return `${conditions.map(conditionPhrase).join(', and ')}.`;
  }

  return '';
};
